/* eslint-disable unused-imports/no-unused-vars */
import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import moment from 'moment';
import { TOrder } from '../../types/orders';
import { getOrderDeliveryType, getOrderPaymentType, getOrderingChannel } from '../ordersHistoryStore/types';
import { userStore } from '../userStore';
import { OrdersNotificationsStore } from './notification';
import { defaultPagination } from 'src/constants/main';
import { CALL_CENTER_GROUP, LIVE_ORDERS_TYPES_GROUP, ORDERS_GROUPS, ORDERS_TYPES_GROUP, ORDER_STATUS, ORDER_TYPE } from 'src/constants/order';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import listener from 'src/services/listener';
import { IPagination, TBooleanInNumber } from 'src/types/main';
import { objectMerge } from 'src/utils/formatter';
import { getCancelToken, getOrderStatusGroup, getOrderType, scrollToElement } from 'src/utils/helpers';
import { findSubstringInArray } from 'src/utils/parser';
import { throttle } from 'src/utils/perfomance';
import { AdvancedFilterStore } from 'src/view/components/AdvancedFilter';
import { AdvancedFilterProperties } from 'src/view/components/AdvancedFilter/types';
import { IBranchesModalItem } from 'src/view/containers/Modals/BranchesModal/BranchesModal';
import { DateRanges } from '~/constants/dateTime';
import logger from '~/services/logger';
// import { orderStore } from '../orderStore';

type TFilters = {
  status: ORDERS_GROUPS;
  // order_key?: string;
  search?: string;
  start_date?: number; // timestamp in seconds
  end_date?: number; // timestamp in seconds
  preorder?: number;
  ahead?: number;
  is_delete?: number;
  type: ORDER_TYPE;
};

type TIncomingFilters = {
  status?: ORDERS_GROUPS;
  // order_key?: string;
  search?: string;
  start_date?: number; // timestamp in seconds
  end_date?: number; // timestamp in seconds
  type?: ORDER_TYPE;
};

const defaultFilters: TFilters = {
  status: ORDERS_GROUPS.ALL,
  search: '',
  start_date: undefined,
  end_date: undefined,
  type: ORDER_TYPE.ACTIVE,
  is_delete: 0,
};

export class OrdersStore extends OrdersNotificationsStore {
  advancedFilter: AdvancedFilterStore = new AdvancedFilterStore({
    name: 'live_orders',
    parentStore: this,
    properties: [
      AdvancedFilterProperties.Date,
      AdvancedFilterProperties.OrderType,
      AdvancedFilterProperties.OrderingChannel,
      AdvancedFilterProperties.PaymentMethod,
      AdvancedFilterProperties.ShowClosed,
      AdvancedFilterProperties.SeparateAhead,
    ],
    presetDates: [
      DateRanges.Tomorrow,
      DateRanges.Today,
      DateRanges.Yesterday,
      DateRanges.ThisWeekUntilToday,
      DateRanges.ThisMonthUntilToday,
      DateRanges.Next30Days,
      DateRanges.Next90Days,
    ],
    allowDatesInTheFuture: true,
  });

  constructor() {
    // TODO: [mobx-undecorate] verify the constructor arguments and the arguments of this automatically generated super call
    super();

    makeObservable(this);
  }

  public get persist() {
    return true;
  }

  cancel: Canceler | null = null;
  cancelStack: {
    [key: string]: Canceler | null;
  } = {
    fetchOrders: null,
    fetchOrdersTimestamp: null,
    fetchRequiredActions: null,
  };

  /**
   * Method running immediately after mobx make hydration at the first time
   */
  afterHydration = () => {
    this.fiveMinutesUpdateHandler();
  };

  /**
   * List of loaded pages, required for pagination and lazy loading starting not from the first page.
   *
   * @type {number[]}
   * @memberof OrdersStore
   */
  @observable public pages: number[] = [];
  /**
   * Orders pagination
   *
   * @type {(IPagination<TOrder | undefined>)}
   * @memberof OrdersStore
   */
  @observable public pagination: IPagination<TOrder | undefined> = defaultPagination;

  /**
   * Order filters object.
   *
   * @see TFilters
   * @type {TFilters}
   * @memberof OrdersStore
   */
  @observable public filters: TFilters = defaultFilters;
  /**
   * Array of branches to filter orders.
   * Branch filter separately due to fact that its state should be saved when switching between sections/refreshing page.
   *
   * @type {IBranchesModalItem[]}
   * @memberof OrdersStore
   */
  @persist('list') @observable public branchesFilter: IBranchesModalItem[] = [];

  /**
   * Successful initialization indicator. Order routes cannot be rendered before initialization.
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  @observable public initialLoading: boolean = true;

  /**
   * Variable indicating that callCenter screen is selected.
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  @observable public isCallCenterScreen: boolean = false;
  /**
   * Action to determine the current screen.
   *
   * @see @see ordersStore.isCallCenterScreen
   */
  @action setIsCallCenterScreen = () => {
    this.isCallCenterScreen = history.location.pathname.includes(SCREENS.CALLCENTER());
  };

  /**
   * Variable responsible for displaying orders list.
   * Used to hide list of orders when order editing.
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  @observable public showOrdersList: boolean = true;
  /**
   * Action to change the order list visibility parameter.
   *
   * @see ordersStore.showOrdersList
   */
  @action setShowOrdersList = (visible: boolean) => {
    this.showOrdersList = visible;
  };

  /**
   * Shows whether search is open in callCenter screen
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  @observable isSearchMode: boolean = false;
  /**
   * @see ordersStore.isSearchMode
   * @param {boolean} value
   */
  @action setIsSearchMode = (value: boolean) => {
    this.isSearchMode = value;
  };

  // /**
  //  * Helper that translates to desired url after click on order
  //  * @see SCREENS
  //  * @param {TOrder} order - order
  //  * @param {boolean} [availableEditMode] - indicates a transition to edit mode
  //  */
  @action selectOrder = (order: TOrder, availableEditMode?: boolean) => {
    // this.notificationHandler(order, {});
    // go to url of selected order. If this is a draft, then url will be different
    // history.push(getOrderScreenPath(order.id, availableEditMode || order.order_status_code === ORDER_STATUS.DRAFT), toJS(order));
  };

  /**
   * Helper that is responsible for selecting an order after a change in list of orders, or after first opening
   *
   * @param {TOrder} [order] - order
   */
  private autoSelectOrderHandler = (order?: TOrder) => {
    // if not Orders screen or callCenter screen is open, then we do not process
    const isOrdersScreen: boolean = findSubstringInArray(history.location.pathname, [SCREENS.LIVE_ORDERS(), SCREENS.CALLCENTER()]);
    if (!isOrdersScreen) return;

    let availableRedirectToEditMode: boolean = false;

    if (order) {
      const isEditMode: boolean = [SCREENS.LIVE_ORDER_EDIT(order.id), SCREENS.CALLCENTER_ORDER_EDIT(order.id)].includes(history.location.pathname);

      // open
      if (order.order_status_code === ORDER_STATUS.DRAFT) {
        availableRedirectToEditMode = true;
      } else if (
        [ORDER_STATUS.CANCELED, ORDER_STATUS.COMPLETED, ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code) &&
        isEditMode
      ) {
        availableRedirectToEditMode = false;
      } else if (isEditMode) {
        availableRedirectToEditMode = true;
      }

      // if this is an old order screen and order is in "draft", "waiting for payment" or "on hold" status
      if (
        !this.isCallCenterScreen &&
        [ORDER_STATUS.DRAFT, ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(order.order_status_code)
      ) {
        this.fetchOrders(1);
        return;
      }

      this.selectOrder(order, availableRedirectToEditMode);
      this.scrollToOrder(order.id);
      return;
    }

    const firstOrder: TOrder | undefined = this.pagination.data.length ? this.pagination.data[0] : undefined;

    // select first one from list if it is available, otherwise, we reset selected order
    if (firstOrder) {
      this.selectOrder(firstOrder);
      this.scrollToOrder(firstOrder.id);
    } else {
      // history.push(getOrderScreenPath());
      // orderStore.setOrder(null);
    }
  };

  /**
   * Method for updating  list of orders when a specific order is updated.
   *
   * @param {TOrder | undefined} [updatedOrder]
   * @param {boolean} [filterOrder] - indicates need to filter order from list
   * @param {boolean} [isNew] - indicates that the order is new
   */
  @observable updateOrdersList = (updatedOrder: TOrder | undefined, { filterOrder, isNew }: { filterOrder?: boolean; isNew?: boolean }) => {
    if (!updatedOrder) return;

    if (filterOrder) {
      this.pagination.data = this.pagination.data.filter((order) => !order || Number(order.id) !== Number(updatedOrder.id));
      return;
    }

    const isAlreadyInList: boolean = this.pagination.data.some((order) => order && Number(order.id) === Number(updatedOrder.id));
    if (!isAlreadyInList) {
      this.pagination.data.unshift(updatedOrder);
      return;
    }
    if (isAlreadyInList && isNew) {
      this.pagination.data = this.pagination.data.filter((order) => order && Number(order.id) !== Number(updatedOrder.id));
      this.pagination.data.unshift(updatedOrder);
      return;
    }

    this.pagination.data = this.pagination.data.map((order) =>
      order && Number(order.id) === Number(updatedOrder.id) && moment(order.updated_at).unix() <= moment(updatedOrder.updated_at).unix()
        ? objectMerge(order, updatedOrder)
        : order
    );
  };

  @action updateOrder = (order: TOrder, { isNew = false, viewed = false }) => {
    const group = getOrderStatusGroup(order.order_status_code);

    // indicates that we are currently in order creation/editing mode. When creating/editing, another store is used
    const isCurrentOrderEdit: boolean = [SCREENS.LIVE_ORDER_EDIT(order.id), SCREENS.CALLCENTER_ORDER_EDIT(order.id)].includes(
      history.location.pathname
    );

    // is order is selected now
    const isOrderSelected: boolean = isCurrentOrderEdit;
    // || orderStore.order?.id === order.id;

    // should order be in current list
    let isOrderInCurrentFilters: boolean = false;
    if (this.isCallCenterScreen && this.isSearchMode) {
      const searchValue: string = this.filters.search?.trim() || '';
      const isOrderNumberEntry: boolean = order.order_key.toString().includes(searchValue);
      const isOrderCustomerNumberEntry: boolean = order.customer_mobile.toString().includes(searchValue);

      isOrderInCurrentFilters = isOrderNumberEntry || isOrderCustomerNumberEntry;
    } else if (this.isCallCenterScreen) {
      isOrderInCurrentFilters = this.filters.status === group || this.filters.status === ORDERS_GROUPS.ALL;
    } else {
      isOrderInCurrentFilters =
        (this.filters.status === group || this.filters.status === ORDERS_GROUPS.ALL || this.filters.status === ORDERS_GROUPS.AHEAD) &&
        order.order_status_code !== ORDER_STATUS.WAITING_FOR_PAYMENT &&
        order.order_status_code !== ORDER_STATUS.ON_HOLD &&
        this.orderInAdvanceFilter(order);
    }

    this.updateOrdersList(order, { filterOrder: !isOrderInCurrentFilters, isNew });
    if (isOrderSelected) {
      if (isOrderInCurrentFilters) {
        // update order in order store if it selected
        // const updOrder: TOrder | undefined = this.pagination.data.find((_order) => _order && _order.id === order.id);
      } else {
        this.autoSelectOrderHandler();
      }
    }
  };

  orderInAdvanceFilter = (order: TOrder) => {
    const group = getOrderStatusGroup(order.order_status_code);

    if (
      this.advancedFilter.dateValues?.from &&
      this.advancedFilter.dateValues?.to &&
      !moment(order.order_datetime).isBetween(moment(this.advancedFilter.dateValues.from), moment(this.advancedFilter.dateValues.to), undefined, '[]')
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.ORDERING_CHANNEL?.length &&
      this.advancedFilter.values.ORDERING_CHANNEL.length > 0 &&
      !this.advancedFilter.values.ORDERING_CHANNEL?.includes(
        getOrderingChannel(order.created_by_code, order.aggregator_key as number, order.dinein?.dinein_delivery_type)
      )
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.ORDER_TYPE?.length &&
      this.advancedFilter.values.ORDER_TYPE.length > 0 &&
      !this.advancedFilter.values.ORDER_TYPE?.includes(
        getOrderDeliveryType(order.delivery_type_code, order.dinein?.dinein_delivery_type, !!order.curbside)
      )
    ) {
      return false;
    }

    if (
      this.advancedFilter.values.PAYMENT_METHOD?.length &&
      this.advancedFilter.values.PAYMENT_METHOD.length > 0 &&
      !this.advancedFilter.values.PAYMENT_METHOD?.includes(getOrderPaymentType(order.payment_type, order.aggregator_key as number))
    ) {
      return false;
    }

    if (
      (this.advancedFilter.values.SEPARATE_AHEAD &&
        order.ahead === 0 &&
        this.filters.status !== ORDERS_GROUPS.AHEAD &&
        this.filters.status !== ORDERS_GROUPS.ALL) ||
      (!this.advancedFilter.values.SEPARATE_AHEAD &&
        order.ahead === 0 &&
        this.filters.status !== ORDERS_GROUPS.NEW &&
        this.filters.status !== ORDERS_GROUPS.ALL)
    ) {
      return false;
    }

    if (
      (this.advancedFilter.values.SHOW_CLOSED &&
        group === ORDERS_GROUPS.CLOSED &&
        this.filters.status !== ORDERS_GROUPS.ALL &&
        this.filters.status !== ORDERS_GROUPS.CLOSED) ||
      (!this.advancedFilter.values.SHOW_CLOSED && group === ORDERS_GROUPS.CLOSED)
    ) {
      return false;
    }
    return true;
  };

  @action public switchToOrderFilters = (order: TOrder, options?: { onlyGroup?: boolean; notFetch?: boolean; notSetOrder?: boolean }) => {
    const type = getOrderType(order);
    const group: ORDERS_GROUPS | null = options?.onlyGroup ? null : getOrderStatusGroup(order.order_status_code);
    this.setFilters({ status: group || ORDERS_GROUPS.ALL, type }, false, {
      order_id: options?.notSetOrder ? undefined : order.id,
      notFetch: options?.notFetch,
    });
  };

  /**
   * Branches id array for requesting orders.
   *
   * @readonly
   * @private
   * @type {number[]}
   * @memberof OrdersStore
   */
  private get branchesParams(): number[] {
    // CallCenter screen does not have filter by branches
    if (this.isCallCenterScreen) {
      return userStore.user.branches.map((branch) => branch.id);
    }

    return this.branchesFilter.length ? this.branchesFilter.map((branch) => branch.id) : userStore.user.branches.map((branch) => branch.id);
  }

  /**
   * Order status parameter for requesting orders.
   *
   * @see ORDER_STATUS
   * @readonly
   * @private
   * @type {(ORDER_STATUS[] | undefined)}
   */
  private get statusParams(): ORDER_STATUS[] | undefined {
    switch (true) {
      case this.isCallCenterScreen && !this.filters.search:
        // @ts-ignore
        return CALL_CENTER_GROUP[this.filters.status];
      // search on the callCenter tab works for all statuses
      case this.isCallCenterScreen:
        return undefined;
      case this.filters.type === ORDER_TYPE.DRAFT:
        return ORDERS_TYPES_GROUP.draft[ORDERS_GROUPS.ALL];
      default:
        if (this.filters.status === ORDERS_GROUPS.ALL) {
          if (this.advancedFilter.values.SHOW_CLOSED) {
            return [...LIVE_ORDERS_TYPES_GROUP[ORDERS_GROUPS.ALL], ...LIVE_ORDERS_TYPES_GROUP[ORDERS_GROUPS.CLOSED]];
          }
          return LIVE_ORDERS_TYPES_GROUP[ORDERS_GROUPS.ALL];
        }
        // @ts-ignore
        return LIVE_ORDERS_TYPES_GROUP[this.filters.status];
    }
  }

  /**
   * @readonly
   * @private
   * @type {(TBooleanInNumber | undefined)}
   * @memberof OrdersStore
   */
  private get preorderParams(): TBooleanInNumber | undefined {
    return !this.isCallCenterScreen && this.filters.type === ORDER_TYPE.AHEAD ? 1 : undefined;
  }

  /**
   * @readonly
   * @private
   * @type {(TBooleanInNumber | undefined)}
   * @memberof OrdersStore
   */
  private get aheadParams(): TBooleanInNumber | undefined {
    const SHOW_AHEAD_ONLY = 0;
    const HIDE_AHEAD = 1;
    const SHOW_ALL = undefined;

    if (this.isCallCenterScreen) {
      return SHOW_ALL;
    }

    if (this.advancedFilter.values.SEPARATE_AHEAD && this.filters.status === ORDERS_GROUPS.AHEAD) {
      return SHOW_AHEAD_ONLY;
    }

    if (
      this.filters.status === ORDERS_GROUPS.ALL ||
      this.filters.status === ORDERS_GROUPS.CLOSED ||
      (!this.advancedFilter.values.SEPARATE_AHEAD && this.filters.status === ORDERS_GROUPS.NEW)
    ) {
      return SHOW_ALL;
    }

    return HIDE_AHEAD;
  }

  @action public fetchOrders = async (page: number = this.pagination.current_page + 1, { order_id }: { order_id?: TOrder['id'] } = {}) => {
    return Promise.resolve();
    // try {
    //   this.setIsCallCenterScreen();
    //   if (this.pagination.loading && this.cancelStack.fetchOrders) this.cancelStack.fetchOrders('Cancelled');
    //   if (typeof page !== 'number') page = this.pagination.current_page + 1;

    //   this.pagination.loading = true;
    //   this.pagination.refreshing = page <= 1;

    //   let order: TOrder | undefined = undefined;
    //   if (order_id) {
    //     order = await client.get(`/v3/orders/${order_id}/details`).catch((err: any) => {
    //       // if error is related to "Order not found" we just ignore this message and keep loading main order
    //       if (err.isAxiosError && err.response?.status === 404) {
    //         return undefined;
    //       }
    //       throw err;
    //     });
    //     if (order) {
    //       this.filters.type = getOrderType(order);
    //       if (this.filters.status !== ORDERS_GROUPS.ALL) {
    //         this.filters.status = getOrderStatusGroup(order.order_status_code);
    //       }
    //     }
    //   }

    //   const params: any = this.filters.search
    //     ? {
    //       page,
    //       branches: this.branchesParams,
    //       search: this.filters.search,
    //       per_page: 30,
    //     }
    //     : {
    //       page,
    //       status: this.statusParams,
    //       branches: this.branchesParams,
    //       // preorder: this.preorderParams,
    //       ahead: this.aheadParams,
    //       is_delete: this.filters.is_delete,
    //       order_id,
    //       per_page: 30,
    //     };

    //   if (!this.isCallCenterScreen && !this.filters.search) {
    //     params.due_start_date = this.advancedFilter.dateValues?.from;
    //     params.due_end_date = this.advancedFilter.dateValues?.to;
    //     params.payment_types = this.advancedFilter.values.PAYMENT_METHOD;
    //     params.delivery_types = this.advancedFilter.values.ORDER_TYPE;
    //     params.order_channels = this.advancedFilter.values.ORDERING_CHANNEL;
    //   }

    //   // CallCenter screen and Draft tab does not have filters by date.
    //   if (this.filters.type === ORDER_TYPE.DRAFT || this.isCallCenterScreen) {
    //     delete params.start_date;
    //     delete params.end_date;
    //   }
    //   if (!params.order_id) {
    //     delete params.order_id;
    //   }

    //   const pagination: IPagination<TOrder> = await client.get('/v3/orders', {
    //     params,
    //     cancelToken: getCancelToken((c) => (this.cancelStack.fetchOrders = c)),
    //   });

    //   let data: TOrder[] = [];
    //   if (pagination.current_page === 1 && !this.pages.length) {
    //     // first page
    //     this.pages = [];
    //     data = pagination.data;
    //   } else if (pagination.current_page <= 1 && !pagination.data.length) {
    //     this.pages = [];
    //     data = pagination.data;
    //   } else if (!this.pagination.current_page) {
    //     data = pagination.data;
    //     this.pages = [];
    //   } else if (this.pagination.current_page > page) {
    //     data = arrayMerge(pagination.data, this.pagination.data, 'id') as TOrder[];
    //   } else {
    //     // increase page
    //     data = arrayMerge(this.pagination.data, pagination.data, 'id') as TOrder[];
    //   }

    //   // update collection of orders id's
    //   data.forEach((_order) => {
    //     if (_order) {
    //       runInAction(() => {
    //         this.orders[_order.id] = _order.id;
    //       })
    //     }
    //   });

    //   this.pages = [...this.pages, pagination.current_page].sort((a, b) => (a > b ? 1 : -1));
    //   this.lastOrdersUpdate = Date.now();
    //   this.pagination = {
    //     ...pagination,
    //     loading: false,
    //     refreshing: false,
    //     data,
    //   };

    //   // if there are orders, but there is no selected one and we are on ordersScreen
    //   if (!order && order_id && (history.location.pathname === getOrderScreenPath() || this.pages.length === 1)) {
    //     // if an id was passed for selection
    //     history.push(getOrderScreenPath(order_id));
    //   } else if (order || this.pages.length === 1) {
    //     // if an order has been selected or if this is the first load
    //     this.autoSelectOrderHandler(order);
    //   }

    //   // update last update time
    //   this.setSocketDisconnectLastUpdateTime(data);

    //   this.initialLoading = false;
    // } catch (err: any) {
    //   if (err.message === 'Cancelled') return;
    //   console.info(err, err.message);
    //   this.pagination.loading = false;
    //   this.pagination.refreshing = false;

    //   // network error
    //   if (err.isAxiosError && (err?.response?.status === 500 || err.message === 'Network Error')) return;

    //   // logout if orders branch filter contains invalid branches
    //   if (findSubstring(err.response?.data?.message, 'branches') && findSubstring(err.response?.data?.message, 'invalid')) {
    //     messagesStore.addMessage({
    //       hideCloseIcon: true,
    //       Content: BranchAccessMessage,
    //       contentClassName: 'branch-access-modal',
    //     });
    //   }

    //   modalStore.openErrorModal({
    //     message: getMessageFromError(err),
    //   });
    // }
  };

  /**
   * Method scrolling list of orders to selected order.
   *
   * @param {TOrder['id']} [id]
   */
  private scrollToOrder = (id?: TOrder['id']) => {
    if (id) scrollToElement({ selector: `#order-${id}`, scrollParams: { block: 'center' } });
  };

  /**
   * Action deleting a draft order.
   *
   * @param {TOrder['id']} id
   */
  @action deleteDraft = async (id: TOrder['id']) => {
    try {
      await client.delete(`/v3/orders/${id}`);
      this.pagination.data = this.pagination.data.filter((order) => order?.id !== id);
      this.fetchOrders(this.pagination.current_page, {});
    } catch (error: any) {
      logger.error({ error, place: 'OrdersStore deleteDraft' });
    }
  };

  /**
   * Variable responsible for displaying the "Required action" tab in CallCenter screen.
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  @observable public showActionRequiredTab: boolean = false;
  /**
   * Method of checking for orders requiring attention.
   * Needed to display tab "Requiring action" in callCenter screen.
   *
   * @see ordersStore.showActionRequiredTab
   */
  @action fetchActionRequiredOrder = async () => {
    try {
      const params: any = {
        page: 1,
        status: CALL_CENTER_GROUP[ORDERS_GROUPS.ACTION_REQUIRED],
        branches: this.branchesParams,
        per_page: 1,
      };

      const res = await client.get('/v3/orders', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelStack.fetchRequiredActions = c)),
      });

      this.showActionRequiredTab = Boolean(res?.data?.length);
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'OrdersStore fetchActionRequiredOrder' });
    }
  };

  /**
   * Orders filters update method.
   *
   * @see TIncomingFilters
   * @param {TIncomingFilters} filters - new filters object
   * @param {boolean} clear - reset filters to default values
   * @param {TOrder['id']} [options.order_id] - id for list of orders including specific
   * @param {IBranchesModalItem[]} [options.branches] - array of branches to update branches filter
   * @param {boolean} [options.notFetch] - update filters without calling API
   */
  @action public setFilters = (
    filters: TIncomingFilters,
    clear: boolean,
    options: { order_id?: TOrder['id']; branches?: IBranchesModalItem[]; notFetch?: boolean } = {}
  ) => {
    this.filters = clear ? defaultFilters : { ...this.filters, ...filters };

    if (clear) this.branchesFilter = [];
    if (options.branches && !clear) this.branchesFilter = options.branches;
    this.pagination.current_page = 0;
    if (!options.notFetch) this.fetchOrders(1, { order_id: options.order_id });
  };

  /**
   * Methods for resetting store variables, canceling requests.
   * Separated in order not to reset the branch filter when clearing all store.
   */
  @action clear = (cancelable: boolean) => {
    // if (cancelable && this.cancel) this.cancel();
    if (this.ordersUpdateTimer) clearTimeout(this.ordersUpdateTimer);
    Object.values(this.cancelStack).forEach((token) => (token ? token('Cancelled') : null));
    this.pages = [];
    this.filters = defaultFilters;
    this.pagination = defaultPagination;
    this.clearNotifications();
  };
  @action clearStore = () => {
    this.branchesFilter = [];
  };

  /**
   *
   *
   * Mechanism of updating list of orders every 5 minutes, as well as updates after hydration.
   * Check runs every minute.
   *
   *
   */
  private fiveMinutesUpdateHandler = () => {
    if (this.ordersUpdateTimer) clearTimeout(this.ordersUpdateTimer);
    // ignore checking if user is unauthorized
    if (userStore.isLogin) {
      // if orders list not was updated more than 5 minutes we should refresh the list
      const diff = moment().diff(moment(this.lastOrdersUpdate), 'minutes');
      if (diff > 5) {
        this.refreshOrders();
      }
    }
    this.ordersUpdateTimer = setTimeout(this.fiveMinutesUpdateHandler, 60000);
  };

  /**
   * Last update time.
   *
   * @see ordersStore.fiveMinutesUpdateHandler
   * @private
   * @type {(null | number)}
   * @memberof OrdersStore
   */
  private lastOrdersUpdate: null | number = null;

  /**
   * Order list update timer.
   *
   * @see ordersStore.fiveMinutesUpdateHandler
   * @private
   * @type {(NodeJS.Timeout | null)}
   * @memberof OrdersStore
   */
  private ordersUpdateTimer: NodeJS.Timeout | null = null;

  @action public refreshOrders = throttle(() => {
    const order_id = history.location.pathname.split('/')[3];
    this.pages = [];
    this.fetchOrders(1, {
      order_id,
    });
  }, 2000);

  /**
   *
   *
   * Order update mechanism when socket server is not working.
   * If socket will have some connection issues we will fetch receive new orders from server be request.
   * This method should be called each 30seconds to prevent losing orders.
   *
   *
   */

  /**
   * If order update time is later than last socket update time, then overwrite it.
   *
   * @param {TOrder[]} _orders
   */
  public setSocketDisconnectLastUpdateTime = (_orders: TOrder[]) => {
    _orders.forEach((_order) => {
      const _updateTime: number = moment(_order?.updated_at).isAfter(_order?.created_at, 'second')
        ? moment(_order?.updated_at).unix() * 1000
        : moment(_order?.created_at).unix() * 1000;

      if (moment(_updateTime).isAfter(this.socketDisconnectLastUpdateTime, 'second')) {
        this.socketDisconnectLastUpdateTime = _updateTime;
      }
    });
  };
  /**
   * API request start timer
   *
   * @type {(NodeJS.Timeout | null)}
   * @memberof OrdersStore
   */
  socketDisconnectTimer: NodeJS.Timeout | null = null;

  /**
   * Mechanism operation indicator.
   *
   * @type {boolean}
   * @memberof OrdersStore
   */
  socketDisconnectCheckUpdates: boolean = false;

  /**
   * Time when last order was updated.
   *
   * @type {number}
   * @memberof OrdersStore
   */
  @observable socketDisconnectLastUpdateTime: number = 0;

  @action runCheckingOrdersUpdates = (once: boolean) => {
    return;
    // try {
    //   console.info('---------- CHECKING UPDATES ----------');

    //   // to prevent calls when screen with orders is not selected
    //   if (!findSubstringInArray(history.location.pathname, [ SCREENS.LIVE_ORDERS(), SCREENS.CALLCENTER()])) {
    //     this.stopCheckingOrdersUpdates();
    //   }
    //   // to prevent parallel calling
    //   if (this.socketDisconnectTimer) {
    //     clearTimeout(this.socketDisconnectTimer);
    //   }

    //   const pagination: IPagination<TOrder> = await client.get('/v3/orders', {
    //     params: {
    //       from_timestamp: moment(this.socketDisconnectLastUpdateTime).unix(),
    //       per_page: PER_PAGE.LARGE,
    //       branches: this.branchesParams,
    //       page: 1,
    //     },
    //     cancelToken: getCancelToken((c) => (this.cancelStack.fetchOrdersTimestamp = c)),
    //   });
    //   // wait 2 sec to prevent running notification from socket and api in the same time
    //   await delay(2000);

    //   pagination.data.forEach((_order) => {
    //     // if an update came for an already read order, make it unread
    //     const order: TOrder = { ..._order, viewed: orderStore.order?.id === _order.id ? 1 : _order.viewed };
    //     let event: TOrderCRUDEvents = 'created';

    //     // if order was created before time of last update, then this is not a new order
    //     if (!moment(moment(order.created_at).unix() * 1000).isAfter(this.socketDisconnectLastUpdateTime, 'second')) {
    //       event = 'updated';
    //     }
    //     // we should check if orders already received and prevent misunderstanding
    //     // if orders was already received by this user we should not call notifications again
    //     if (this.orders[order.id]) {
    //       event = 'updated';
    //     }

    //     // if event created it should stay as is
    //     // because order should appear in the list
    //    if (event !== 'created' && !order.viewed) {
    //       if (order.notification_type === ORDER_NOTIFICATION_TYPE.CLIENT_ARRIVED) event = 'client_arrived';
    //       if (order.notification_type === ORDER_NOTIFICATION_TYPE.DRIVER_NOT_FOUND) event = 'driver_not_found';
    //     }

    //      this.onReceiveOrder(order, {event});
    //   });
    //   // update last update time
    //   this.setSocketDisconnectLastUpdateTime(pagination.data);
    // } catch (error: any) {
    //   logger.error({ error, place: 'OrdersStore runCheckingOrdersUpdates' });
    // } finally {
    //   if (!once && this.socketDisconnectCheckUpdates) {
    //     this.socketDisconnectTimer = setTimeout(() => this.runCheckingOrdersUpdates(false), 15000);
    //   }
    // }
  };
  @action stopCheckingOrdersUpdates = () => {
    this.socketDisconnectCheckUpdates = false;
    if (this.socketDisconnectTimer) clearTimeout(this.socketDisconnectTimer);
  };

  orderScreenFocusListener: null | string = null;
  /**
   * Subscribe to focus listener on orders screen.
   * Will call ordersStore.refreshOrders each time when out switching between tabs or change RN app state (controlled by listener).
   *
   * IMPORTANT! - do not remove throttle, it is required to prevent to many calls (focus listener has issue with this).
   */
  @action addCheckingOrdersListener = () => {
    if (!this.orderScreenFocusListener) {
      this.orderScreenFocusListener = listener.addListener('focus', () => ordersStore.runCheckingOrdersUpdates(true), {
        _throttle: 5000,
      });
    }
  };

  @action removeCheckingOrdersListener = () => {
    if (this.orderScreenFocusListener) {
      listener.removeListener(this.orderScreenFocusListener);
      this.orderScreenFocusListener = null;
    }
  };

  @action
  handleAdvancedFilter = () => {
    const isClosedTabActiveButClosedDisabled = this.filters.status === ORDERS_GROUPS.CLOSED && !this.advancedFilter.values.SHOW_CLOSED;
    const isAheadTabActiveButAheadNotSeparated = this.filters.status === ORDERS_GROUPS.AHEAD && !this.advancedFilter.values.SEPARATE_AHEAD;

    if (isClosedTabActiveButClosedDisabled || isAheadTabActiveButAheadNotSeparated) {
      this.setFilters({ status: ORDERS_GROUPS.ALL }, false);
    }
    this.pages = [];
    this.fetchOrders(1);
  };
}

export const ordersStore = new OrdersStore();
